import { computePosition, offset } from '@floating-ui/dom';
import { Extension } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { setRectOverride } from './slash-command';

/** Mirror of the same helper in @tiptap/extension-drag-handle */
function getOuterDomNode(view: EditorView, domNode: HTMLElement): HTMLElement {
	let node = domNode;
	while (node?.parentNode) {
		if (node.parentNode === view.dom) break;
		node = node.parentNode as HTMLElement;
	}
	return node;
}

export const PlusButton = Extension.create({
	name: 'plusButton',

	addProseMirrorPlugins() {
		const editor = this.editor;

		// ── build elements ──────────────────────────────────────────────────
		const button = document.createElement('button');
		button.type = 'button';
		button.textContent = '+';
		Object.assign(button.style, {
			width: '24px',
			height: '24px',
			borderRadius: '6px',
			background: '#eee',
			cursor: 'pointer',
			// Start hidden – same visibility/pointerEvents pattern as drag handle
			visibility: 'hidden',
			pointerEvents: 'none',
		});

		const wrapper = document.createElement('div');
		Object.assign(wrapper.style, {
			position: 'absolute',
			top: '0',
			left: '0',
			pointerEvents: 'none',
		});
		wrapper.appendChild(button);

		// ── state ────────────────────────────────────────────────────────────
		let currentNode: Node | null = null;
		let currentNodePos = -1;
		let rafId: number | null = null;
		let pendingCoords: { x: number; y: number } | null = null;

		// ── helpers ──────────────────────────────────────────────────────────
		function hideButton() {
			button.style.visibility = 'hidden';
			button.style.pointerEvents = 'none';
		}

		function showButton() {
			if (!editor.isEditable) {
				hideButton();
				return;
			}
			button.style.visibility = '';
			button.style.pointerEvents = 'auto';
		}

		/**
		 * Mirrors repositionDragHandle.
		 * Uses left-start / absolute like the drag handle, but adds an extra
		 * mainAxis offset so the plus button sits to the left of the drag handle
		 * (drag handle ≈ 24 px wide + 8 px breathing room = 32 px).
		 */
		function repositionButton(dom: Element) {
			computePosition(
				{ getBoundingClientRect: () => dom.getBoundingClientRect() },
				button,
				{
					placement: 'left-start',
					strategy: 'absolute',
					middleware: [offset(32)],
				},
			).then((val) => {
				Object.assign(button.style, {
					position: val.strategy,
					left: `${val.x}px`,
					top: `${val.y}px`,
				});
			});
		}

		return [
			new Plugin({
				view: (view) => {
					editor.view.dom.parentElement?.appendChild(wrapper);

					button.onclick = () => {
						if (!currentNode || currentNodePos === -1) return;

						const raw = view.nodeDOM(currentNodePos) as HTMLElement | null;
						if (!raw) return;
						const domNode = getOuterDomNode(view, raw);

						// Tell the slash-command popup where to anchor itself.
						setRectOverride(() => domNode.getBoundingClientRect());

						// Insert a new paragraph immediately below the hovered block.
						const insertAt = currentNodePos + currentNode!.nodeSize;
						editor
							.chain()
							.focus()
							.insertContentAt(insertAt, {
								type: 'paragraph',
								content: [{ type: 'text', text: '/' }],
							})
							.run();
					};

					return {
						/** Mirrors drag-handle update() – reposition when doc changes */
						update(_, oldState) {
							if (!editor.isEditable) {
								hideButton();
								return;
							}
							if (view.state.doc.eq(oldState.doc) || currentNodePos === -1)
								return;

							let domNode = view.nodeDOM(currentNodePos) as HTMLElement | null;
							if (!domNode) return;
							domNode = getOuterDomNode(view, domNode);
							if (domNode === view.dom || domNode?.nodeType !== 1) return;

							repositionButton(domNode);
						},

						destroy() {
							if (rafId) cancelAnimationFrame(rafId);
							wrapper.remove();
						},
					};
				},

				props: {
					handleDOMEvents: {
						/** Mirrors drag-handle keydown – hide when user starts typing */
						keydown(view) {
							if (view.hasFocus()) {
								hideButton();
								currentNode = null;
								currentNodePos = -1;
							}
							return false;
						},

						/** Mirrors drag-handle mouseleave – keep visible while inside wrapper */
						mouseleave(_view, e) {
							if (!wrapper.contains(e.relatedTarget as HTMLElement)) {
								hideButton();
								currentNode = null;
								currentNodePos = -1;
							}
							return false;
						},

						/** Mirrors drag-handle mousemove – RAF-throttled, posAtCoords + getOuterDomNode */
						mousemove(view, e) {
							pendingCoords = { x: e.clientX, y: e.clientY };
							if (rafId) return false;

							rafId = requestAnimationFrame(() => {
								rafId = null;
								if (!pendingCoords || !editor.isEditable) return;

								const { x, y } = pendingCoords;
								pendingCoords = null;

								const result = view.posAtCoords({ left: x, top: y });
								if (!result) return;

								let $pos;
								try {
									$pos = view.state.doc.resolve(result.pos);
								} catch {
									return;
								}

								if ($pos.depth === 0) return;

								const nodePos = $pos.before(1);
								let domNode = view.nodeDOM(nodePos) as HTMLElement | null;
								if (!domNode) return;

								domNode = getOuterDomNode(view, domNode);
								if (domNode === view.dom || domNode?.nodeType !== 1) return;

								const node = view.state.doc.nodeAt(nodePos);
								if (!node) return;

								if (node !== currentNode) {
									currentNode = node;
									currentNodePos = nodePos;
									repositionButton(domNode);
									showButton();
								}
							});

							return false;
						},
					},
				},
			}),
		];
	},
});
